/**
 * The open map's state in the engine (Phase 5b, step 3): its tree (the
 * projection of the map's CRDT), its id, whether it is shared and this
 * person's role, the links it holds. Engine code reads and sets it here; the
 * interface keeps its screen state apart (focus, selection, view, zoom…) and
 * follows this one (interfaces/classic/store/index.js listens to MAP_STATE_CHANGED).
 *
 * After every change the tree keeps the objects of items the change did not
 * touch, and the change is described (engine/core/tree-reconcile.js).
 *
 * The functions engine modules call keep their old names where they already
 * had them (`_setDataFromCRDT`, `_updateNodeDirect`): the CRDT bridge passes
 * this module where it passed the Store.
 */
import { emit, Events } from "./core/events.js";
import * as dataModel from "./core/data-model.js";
import { reconcileTree } from "./core/tree-reconcile.js";
import { validateTree } from "./core/validation.js";
import { isDocQuotaBlocked, setQuotaActiveDoc } from "./services/quota-state.js";
import { isActiveShareReadOnly, isActiveShareDeleteBlocked } from "./sync/share-state.js";
import { isMapFull as mapIsFull } from "./services/map-size.js";
import { createLogger } from "./core/logger.js";

const log = createLogger("MapState", "warn");

const EMPTY = Object.freeze({ data: null, currentDocId: null, isShared: false, shareRole: null });
let state = EMPTY;

// Reference nodes: nodeId -> the linked map's id.
const linkedDocs = new Map();

/** The state (a copy): { data, currentDocId, isShared, shareRole }. */
export function getState() {
  return { ...state };
}

/** One field of the state. */
export function get(key) {
  return state[key];
}

export const find = dataModel.find;
export const findParent = dataModel.findParent;
export const getAll = dataModel.getAll;
export const getPath = dataModel.getPath;
export const getLeaves = dataModel.getLeaves;

/**
 * Change the state and tell who follows it (MAP_STATE_CHANGED):
 * { state, previous, reason, …details }.
 */
function change(updates, details) {
  const previous = state;
  state = { ...state, ...updates };
  if ("currentDocId" in updates) setQuotaActiveDoc(updates.currentDocId);
  emit(Events.MAP_STATE_CHANGED, { state, previous, ...details });
}

function indexLinks(tree) {
  linkedDocs.clear();
  const walk = (node) => {
    if (node.linkedDocId) linkedDocs.set(node.id, node.linkedDocId);
    const children = node.children;
    if (children) for (let i = 0; i < children.length; i++) walk(children[i]);
  };
  if (tree) walk(tree);
}

/**
 * A map was opened: its tree replaces the last one (nothing compared).
 * @param {Object} tree
 * @param {{docId?: string|null, isShared?: boolean, shareRole?: string|null}} [options]
 */
export function open(tree, { docId = null, isShared = false, shareRole = null } = {}) {
  dataModel.setRoot(tree);
  indexLinks(tree);
  change({ data: tree, currentDocId: docId, isShared, shareRole }, { reason: "open" });
  emit(Events.MAP_OPENED, { docId });
}

/**
 * The tree built again from the map after a change (the CRDT observer, a
 * batch, a shared map's update). Returns false when the tree is rejected
 * (invalid: duplicate ids…), and nothing changes.
 *
 * @param {Object} tree
 * @param {Object} [options]
 * @param {string} [options.source] - where the change came from ("remote", "batch"…)
 * @param {boolean} [options.isInitialLoad] - the map's first tree: DATA_LOADED (resets history), nothing compared
 * @param {string|null} [options.docId] - set the map's id (null: keep)
 * @param {boolean|null} [options.isShared] - set whether it is shared (null: keep)
 * @param {string|null} [options.shareRole] - set the role (undefined: keep)
 * @param {boolean} [options.preserveFocus] / [options.preserveSelection] - for the interface's screen state
 */
export function setTree(tree, options = {}) {
  if (!tree) {
    log.warn("setTree called without a tree");
    return false;
  }
  const {
    source = "crdt",
    isInitialLoad = false,
    docId = null,
    isShared = null,
    shareRole = undefined,
    preserveFocus = true,
    preserveSelection = true,
  } = options;
  if (!validateTree(tree, (msg) => log.error("validateTree:", msg))) {
    log.error("setTree received an invalid tree: not applied");
    return false;
  }
  // Items this change left alone keep their objects (same map only).
  let changes = null;
  if (!isInitialLoad && state.data && state.data.id === tree.id) {
    ({ tree, changes } = reconcileTree(state.data, tree));
  }
  const previousTree = state.data;
  dataModel.setRoot(tree);
  indexLinks(tree);
  const updates = { data: tree };
  if (docId !== null) updates.currentDocId = docId;
  if (isShared !== null) updates.isShared = isShared;
  if (shareRole !== undefined) updates.shareRole = shareRole;
  change(updates, { reason: "tree", source, changes, previousTree, preserveFocus, preserveSelection, isBatchUpdate: source === "batch" });
  // DATA_LOADED resets history, CRDT_PROJECTION_UPDATED does not.
  if (isInitialLoad) {
    emit(Events.DATA_LOADED, { data: tree });
    emit(Events.MAP_OPENED, { docId: state.currentDocId });
  } else {
    emit(Events.CRDT_PROJECTION_UPDATED, { data: tree, changes, origin: source });
  }
  return true;
}

/** The name engine modules call (store-rebuild.js, share-lifecycle.js…). */
export const _setDataFromCRDT = setTree;

/**
 * Fields kept on this device only (sizes, a fold kept on this device in a
 * shared map), written into the item in place: no CRDT write, no history.
 * @param {string} nodeId
 * @param {Object} updates
 * @param {{dimensionOnly?: boolean}} [options] - false when the change shows
 */
export function updateLocalFields(nodeId, updates, { dimensionOnly = true } = {}) {
  const node = dataModel.find(nodeId);
  if (!node) return;
  const oldValues = {};
  for (const key of Object.keys(updates)) if (node[key] !== undefined) oldValues[key] = node[key];
  Object.assign(node, updates);
  // A new root object: who follows sees a change (as before).
  change({ data: { ...state.data } }, { reason: "local-fields", dimensionOnly });
  emit(Events.NODE_UPDATED, { nodeId, updates, oldValues, source: "direct" });
}

/** The name engine modules call (node-operations.js, batch-operations.js). */
export const _updateNodeDirect = updateLocalFields;

/** This person's role on the open map changed (a live role change). */
export function setShareRole(role) {
  if (state.shareRole !== role) change({ shareRole: role }, { reason: "role" });
}

/** The open map's id. */
export function setCurrentDocId(docId) {
  change({ currentDocId: docId }, { reason: "map-id" });
}

/** No map open. */
export function reset() {
  dataModel.setRoot(null);
  linkedDocs.clear();
  const previous = state;
  state = EMPTY;
  emit(Events.MAP_STATE_CHANGED, { state, previous, reason: "reset" });
}

/**
 * The open map, or this person, cannot change it: a viewer, the storage
 * quota. (A full map too: engine/services/map-size.js, isMapFull.)
 */
export function isReadOnly() {
  return isActiveShareReadOnly() || isDocQuotaBlocked(state.currentDocId);
}

/** Read-only in a way that also stops deleting and cutting (a full map allows them, to make room). */
export function isDeleteBlocked() {
  return isActiveShareDeleteBlocked() || isDocQuotaBlocked(state.currentDocId);
}

/** The open map has reached the size limit. */
export function isMapFull() {
  return mapIsFull(state.currentDocId);
}

// ---------------------------------------------------------------------------
// Links to other maps
// ---------------------------------------------------------------------------

export function isReferenceNode(nodeId) {
  return linkedDocs.has(nodeId);
}

export function getLinkedDocId(nodeId) {
  return linkedDocs.get(nodeId) || null;
}

/** Whether a node belongs to a linked map (under a reference node). */
export function isNodeInLinkedDoc(nodeId) {
  return getLinkedDocIdForNode(nodeId) !== null;
}

/** The linked map holding this node, or null. */
export function getLinkedDocIdForNode(nodeId) {
  let current = dataModel.find(nodeId);
  while (current) {
    const parent = dataModel.findParent(current.id);
    if (parent && parent.linkedDocId) return parent.linkedDocId;
    current = parent;
  }
  return null;
}

export function initLinkedDocsFromTree(rootNode) {
  indexLinks(rootNode);
}

export function clearLinkedDocs() {
  linkedDocs.clear();
}
